import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, ReactNode } from 'react';

export type ModalSize = 'small' | 'medium' | 'large' | 'full';
export type ButtonVariant = 'primary' | 'secondary' | 'secondaryOutlined' | 'secondaryRound' | 'primaryRound';

export interface ModalButton {
    text: string;
    onClick: () => void;
    variant?: ButtonVariant;
    closeOnClick?: boolean;
}

export interface ButtonConfig {
    text: string;
    onClick: () => void;
    variant?: ButtonVariant;
    key?: string;
}

export interface ModalConfig {
    title: string;
    content: string | ReactNode;
    size?: ModalSize;
    showCloseButton?: boolean;
    primaryButton?: ModalButton;
    secondaryButton?: ModalButton;
    customButtons?: ReactNode[]; // can use tsx
    buttonConfigs?: ButtonConfig[]; // cant use tsx
    closeOnBackdropClick?: boolean;
    onClose?: () => void;
    shouldScrollToBottom?: boolean;
    onContentRendered?: () => void;
}

interface ModalContextType {
    openModal: (config: ModalConfig) => void;
    closeModal: () => void;
    isOpen: boolean;
    config: ModalConfig | null;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export const ModalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [config, setConfig] = useState<ModalConfig | null>(null);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }

        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    const openModal = useCallback((newConfig: ModalConfig) => {
        setConfig(newConfig);
        setIsOpen(true);
    }, []);

    const closeModal = useCallback(() => {
        setIsOpen(false);
        config?.onClose?.();
        setTimeout(() => setConfig(null), 300);
    }, [config]);

    const value = useMemo(() => ({ openModal, closeModal, isOpen, config }), [openModal, closeModal, isOpen, config]);

    return <ModalContext.Provider value={value}>{children}</ModalContext.Provider>;
};

export const useModalContext = () => {
    const context = useContext(ModalContext);
    if (!context) {
        throw new Error('useModalContext must be used within a ModalProvider');
    }
    return context;
};
